import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import Column from '../components/box/column';
import SizedBox from '../components/box/sizedBox';
import { LXbody, LXCaption } from './lxText';

interface LXInnerInputProps {
  width?: number;
  height?: number;
  borderColor?: string;
  type?: string;
}

const LXInnerInput = styled.input<LXInnerInputProps>`
  width: ${(props) => `${props.width}px`};
  height: ${(props) => `${props.height}px`};
  padding: 13px 15px 15px 15px;
  font-size: 14px;
  font-family: 'Outfit';
  font-style: normal;
  color: ${(props) => props.theme.colors.body};
  line-height: 18px;
  border-radius: 12px;
  border: ${(props) => `1px solid ${props.theme.colors.grayLine}`};
  border-color: ${(props) => props.borderColor};

  &::placeholder {
    font-size: 14px;
    font-family: 'Outfit';
    font-style: normal;
    color: ${(props) => props.theme.colors.placeHolder};
    line-height: 18px;
  }

  &:focus {
    width: ${(props) => `${props.width}px`};
    padding: 13px 15px 15px 15px;
    font-size: 14px;
    font-family: 'Outfit';
    font-style: normal;
    color: ${(props) => props.theme.colors.body};
    line-height: 18px;
    border-radius: 12px;
    outline: none !important;
    border: ${(props) =>
      `1px solid ${props.borderColor ?? props.theme.colors.primary500}`};
  }

  &:disabled {
    width: ${(props) => `${props.width}px`};
    padding: 13px 15px 15px 15px;
    font-size: 14px;
    font-family: 'Outfit';
    font-style: normal;
    color: ${(props) => props.theme.colors.disabled};
    background-color: ${(props) => props.theme.colors.grayBgLight};
    line-height: 18px;
    border-radius: 12px;
    outline: none !important;
    border: ${(props) => `1px solid ${props.theme.colors.grayLine}`};
  }
`;

const LXInnerTextArea = styled.textarea<LXInnerInputProps>`
  width: ${(props) => `${props.width}px`};
  height: ${(props) => `${props.height}px`};
  padding: 13px 15px 15px 15px;
  font-size: 14px;
  font-family: 'Outfit';
  font-style: normal;
  color: ${(props) => props.theme.colors.body};
  line-height: 18px;
  border-radius: 12px;
  border: ${(props) => `1px solid ${props.theme.colors.grayLine}`};
  border-color: ${(props) => props.borderColor};

  &::placeholder {
    font-size: 14px;
    font-family: 'Outfit';
    font-style: normal;
    color: ${(props) => props.theme.colors.placeHolder};
    line-height: 18px;
  }

  &:focus {
    width: ${(props) => `${props.width}px`};
    padding: 13px 15px 15px 15px;
    font-size: 14px;
    font-family: 'Outfit';
    font-style: normal;
    color: ${(props) => props.theme.colors.body};
    line-height: 18px;
    border-radius: 12px;
    outline: none !important;
    border: ${(props) =>
      `1px solid ${props.borderColor ?? props.theme.colors.primary500}`};
  }

  &:disabled {
    width: ${(props) => `${props.width}px`};
    padding: 13px 15px 15px 15px;
    font-size: 14px;
    font-family: 'Outfit';
    font-style: normal;
    color: ${(props) => props.theme.colors.disabled};
    background-color: ${(props) => props.theme.colors.grayBgLight};
    line-height: 18px;
    border-radius: 12px;
    outline: none !important;
    border: ${(props) => `1px solid ${props.theme.colors.grayLine}`};
  }
`;

interface LXInputProps {
  disabled?: boolean;
  validate?: (arg0: string) => Promise<string>;
  onValidate?: (isValid: boolean) => void;
  onChange?: (value: string) => void;
  validatingDefaultText?: string;
  placeholder?: string;
  value?: string;
  validateDelay?: number;
  isMultiline?: boolean;
  tabIndex?: number;
}

const LXInput: React.FC<LXInputProps & LXInnerInputProps> = (props) => {
  const [validateText, setValidateText] = useState('');
  const theme = useTheme();

  const validatingDefaultText = props.validatingDefaultText ?? 'validate...';
  let timerId: NodeJS.Timeout | undefined;

  const onChagne: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (event) => {
    if (props.onChange !== undefined) {
      props.onChange(event.target.value);
    }
    if (props.validate !== undefined) {
      validate(event.target.value);
    }
  };

  const validate = (newValue: string) => {
    if (props.onValidate !== undefined) {
      props.onValidate!(false);
    }
    clearTimer();
    setValidateText(validatingDefaultText);
    timerId = setTimeout(
      () => realValidate(newValue),
      props.validateDelay ?? 500
    );
  };

  const clearTimer = () => {
    if (timerId !== undefined) {
      clearTimeout(timerId!);
    }
  };

  useEffect(() => {
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const realValidate = async (newValue: string) => {
    props.validate!(newValue)
      .then((validateResult) => {
        setValidateText(validateResult);
        if (props.onValidate !== undefined) {
          props.onValidate!(validateResult === '');
        }
      })
      .catch((_) => {
        setValidateText('');
        if (props.onValidate !== undefined) {
          props.onValidate!(false);
        }
      });
  };

  return (
    <Column>
      {props.isMultiline ? (
        <LXInnerTextArea
          disabled={props.disabled}
          width={props.width}
          placeholder={props.placeholder}
          height={props.height}
          onChange={onChagne}
          borderColor={validateText === '' ? undefined : theme.colors.error}
          value={props.value}
          tabIndex={props.tabIndex}
        ></LXInnerTextArea>
      ) : (
        <LXInnerInput
          disabled={props.disabled}
          type={props.type}
          placeholder={props.placeholder}
          width={props.width}
          height={props.height}
          onChange={onChagne}
          borderColor={validateText === '' ? undefined : theme.colors.error}
          value={props.value}
          tabIndex={props.tabIndex}
        ></LXInnerInput>
      )}

      {validateText !== '' && <SizedBox height={8}></SizedBox>}
      {validateText !== '' && (
        <LXCaption color={theme.colors.error}>{validateText}</LXCaption>
      )}
    </Column>
  );
};

export default LXInput;

interface LXLabelInputProps {
  label: string;
  color?: string;
}

export const LXLabelInput: React.FC<
  LXLabelInputProps & LXInputProps & LXInnerInputProps
> = (props) => {
  const theme = useTheme();

  return (
    <Column>
      <LXbody color={props.color ?? theme.colors.title}>{props.label}</LXbody>
      <SizedBox height={8}></SizedBox>
      <LXInput {...props}></LXInput>
    </Column>
  );
};
